import { useId, useRef, useState, useCallback, useMemo } from 'react';
import { useAnimationFrame } from '../viz/useAnimationFrame';
import {
  usePrefersReducedMotion,
  cueIsActive,
  cueHighlights,
  type VizCue,
} from '../viz/vizShared';

// Realistic test tube with a curved liquid meniscus, optional reaction effects
// (rising gas bubbles, swirl, settling precipitate) and active/result borders.
// Physical glass colors kept; accent/result borders use --ws-* tokens.
//
// UPGRADE: curved meniscus, bubble effervescence, swirl ripple, precipitate, and
// an optional narration `cue` (progress → fill, highlight tokens → effects,
// play → run). All new props are OPTIONAL and additive.

export interface TestTubeProps {
  fillLevel?: number; // 0-100
  fillColor?: string;
  label?: string;
  onClick?: () => void;
  active?: boolean;
  disabled?: boolean;
  hasResult?: boolean;
  /** Rising gas bubbles (effervescence). */
  bubbles?: boolean;
  /** Animated swirl ripple of the surface. */
  swirl?: boolean;
  /** Settled precipitate fraction 0-1. */
  precipitate?: number;
  className?: string;
  /** Narration-sync cue forwarded by the lecture player. */
  cue?: VizCue;
}

export default function TestTube({
  fillLevel = 0,
  fillColor = '#7DD3FC',
  label = '',
  onClick,
  active = false,
  disabled = false,
  hasResult = false,
  bubbles = false,
  swirl = false,
  precipitate = 0,
  className = '',
  cue,
}: TestTubeProps) {
  const id = useId();
  const reduced = usePrefersReducedMotion();
  const cueOn = cueIsActive(cue);
  const tokens = cueHighlights(cue);

  const cueFill = cue?.progress != null ? Math.max(0, Math.min(100, cue.progress * 100)) : null;
  const fill = cueFill ?? fillLevel;
  const playing = cue?.play !== false;
  const cueBubbles = cueOn && (tokens.includes('bubbles') || tokens.includes('gas') || tokens.includes('react') || tokens.includes('fizz'));
  const cueSwirl = cueOn && (tokens.includes('swirl') || tokens.includes('mix') || tokens.includes('stir'));
  const cuePrecip = cueOn && (tokens.includes('precipitate') || tokens.includes('precip')) ? 0.6 : null;

  const bubblesOn = (bubbles || cueBubbles) && fill > 0 && !reduced && playing;
  const swirlOn = (swirl || cueSwirl) && !reduced && playing;
  const precip = cuePrecip ?? precipitate;

  // Liquid geometry. Tube interior runs ~ y=12 (top) to y=80 (rounded bottom).
  const innerTop = 12;
  const bottomY = 80;
  const usable = bottomY - innerTop; // 68
  const surfaceY = bottomY - (fill / 100) * usable;

  const phaseRef = useRef({ t: 0 });
  const [, setTick] = useState(0);
  const bump = useCallback(() => setTick((n) => (n + 1) % 1_000_000), []);
  useAnimationFrame(() => {
    phaseRef.current.t += 1 / 60;
    bump();
  }, bubblesOn || swirlOn);
  const t = phaseRef.current.t;

  const ripple = swirlOn ? Math.sin(t * 3.4) * 1.6 : 0;
  const sy = surfaceY;
  // Curved (concave) meniscus across the tube bore.
  const liquidPath = `M10 ${sy + ripple}
    Q20 ${sy - 2.2} 30 ${sy - ripple}
    L30 70 Q30 80 20 80 Q10 80 10 70 Z`;

  const precipH = precip * 16;

  const bubbleSeeds = useMemo(
    () => Array.from({ length: 6 }, (_, i) => ({
      x: 13 + ((i * 2.6) % 14),
      r: 0.8 + (i % 3) * 0.5,
      speed: 0.8 + (i % 3) * 0.3,
      phase: i / 6,
    })),
    [],
  );

  return (
    <svg
      viewBox="0 0 40 100"
      width={35}
      height={90}
      onClick={disabled ? undefined : onClick}
      role="img"
      aria-label={`Test tube${label ? ` ${label}` : ''}, ${Math.round(fill)}% full`}
      style={{
        cursor: disabled ? 'default' : onClick ? 'pointer' : 'default',
        opacity: disabled ? 0.5 : 1,
        transition: 'all 0.2s ease',
      }}
    >
      <defs>
        <linearGradient id={`tube-glass-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="rgba(200,220,240,0.6)" />
          <stop offset="20%" stopColor="rgba(255,255,255,0.4)" />
          <stop offset="50%" stopColor="rgba(200,220,240,0.3)" />
          <stop offset="80%" stopColor="rgba(255,255,255,0.4)" />
          <stop offset="100%" stopColor="rgba(200,220,240,0.5)" />
        </linearGradient>
        <linearGradient id={`tube-liquid-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={fillColor} stopOpacity="0.8" />
          <stop offset="20%" stopColor={fillColor} stopOpacity="0.9" />
          <stop offset="100%" stopColor={fillColor} stopOpacity="0.97" />
        </linearGradient>
        <linearGradient id={`tube-precip-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#D4C8A8" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#8A7858" stopOpacity="0.95" />
        </linearGradient>
        <clipPath id={`tube-clip-${id}`}>
          <path d="M8 8 L8 70 Q8 82 20 82 Q32 82 32 70 L32 8 Z" />
        </clipPath>
      </defs>

      {(active || hasResult) && (
        <rect
          x="5"
          y="2"
          width="30"
          height="85"
          rx="5"
          fill="none"
          stroke={hasResult ? 'var(--ws-success)' : 'var(--ws-accent)'}
          strokeWidth="2"
        />
      )}

      <ellipse cx="20" cy="8" rx="12" ry="4" fill="rgba(200,220,240,0.5)" stroke="#8BA8C4" strokeWidth="1.5" />
      <path d="M8 8 L8 70 Q8 82 20 82 Q32 82 32 70 L32 8" fill={`url(#tube-glass-${id})`} stroke="#8BA8C4" strokeWidth="1.5" />

      {fill > 0 && (
        <g clipPath={`url(#tube-clip-${id})`}>
          <path d={liquidPath} fill={`url(#tube-liquid-${id})`} />
          {/* Concave meniscus highlight. */}
          <path
            d={`M10 ${sy + ripple} Q20 ${sy - 2.2} 30 ${sy - ripple}`}
            fill="none"
            stroke="rgba(255,255,255,0.6)"
            strokeWidth="0.9"
          />

          {bubblesOn && bubbleSeeds.map((b, i) => {
            const span = Math.max(bottomY - sy - 2, 0);
            const prog = (t * b.speed + b.phase) % 1;
            const by = bottomY - 3 - prog * span;
            const bx = b.x + Math.sin(t * 4 + i) * 0.8;
            return <circle key={i} cx={bx} cy={by} r={b.r} fill="rgba(255,255,255,0.85)" opacity={(1 - prog) * 0.8} />;
          })}

          {precip > 0 && (
            <path
              d={`M10 ${78 - precipH} Q20 ${76 - precipH} 30 ${78 - precipH} L30 70 Q30 80 20 80 Q10 80 10 70 Z`}
              fill={`url(#tube-precip-${id})`}
            />
          )}
        </g>
      )}

      <line x1="11" y1="15" x2="11" y2="65" stroke="rgba(255,255,255,0.5)" strokeWidth="2" strokeLinecap="round" />

      <text x="20" y="96" fontSize="8" fill="var(--ws-ink)" textAnchor="middle" fontWeight="500">
        {label}
      </text>
    </svg>
  );
}
